import { useState, useEffect, useCallback, useRef } from 'react';
import { useScreenData } from './useScreenData';
import { useUser } from '@/contexts/UserContext';

/**
 * Cascading Timezone Hook
 * 
 * Manages timezone preferences with the following hierarchy:
 * 1. User-level override (stored in user preferences)
 * 2. System default (from admin settings)
 * 
 * Data Flow: System Default → User Default → Timeline Dropdown → Booking Form → Saved Booking
 */
export function useTimezone() {
  const { settings, updateSettings } = useScreenData('settings');
  const { user, updateUserTimezone } = useUser();
  
  const prevDefaultRef = useRef(settings.defaultTimezone);

  const getEffectiveTimezone = useCallback((): string => {
    if (user.timezoneOverride) {
      return user.timezoneOverride;
    }
    return settings.defaultTimezone;
  }, [user.timezoneOverride, settings.defaultTimezone]);

  const [effectiveTimezone, setEffectiveTimezone] = useState<string>(getEffectiveTimezone());

  useEffect(() => {
    const newEffective = getEffectiveTimezone();
    setEffectiveTimezone(newEffective);

    const defaultChanged = prevDefaultRef.current !== settings.defaultTimezone;
    prevDefaultRef.current = settings.defaultTimezone;

    if (!user.timezoneOverride && settings.timelineDefaultTimezone !== newEffective && defaultChanged) {
      updateSettings({ timelineDefaultTimezone: newEffective });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEffectiveTimezone, settings.defaultTimezone, user.timezoneOverride]);

  // Set user-level timezone override
  const setUserTimezone = useCallback(async (timezone: string) => {
    await updateUserTimezone(timezone);
    setEffectiveTimezone(timezone);
    
    // Update timeline default to match
    await updateSettings({ timelineDefaultTimezone: timezone });
  }, [updateUserTimezone, updateSettings]);

  // Clear user override and revert to system default
  const clearUserTimezone = useCallback(async () => {
    await updateUserTimezone(null);
    const systemDefault = settings.defaultTimezone;
    setEffectiveTimezone(systemDefault);
    
    // Update timeline default to match system default
    await updateSettings({ timelineDefaultTimezone: systemDefault });
  }, [updateUserTimezone, settings.defaultTimezone, updateSettings]);

  // Check if user has an override set
  const hasUserOverride = useCallback((): boolean => {
    return !!user.timezoneOverride;
  }, [user.timezoneOverride]);

  // Get the system default timezone
  const getSystemDefault = useCallback((): string => {
    return settings.defaultTimezone;
  }, [settings.defaultTimezone]);

  return {
    // Current effective timezone (respects cascade)
    timezone: effectiveTimezone,
    
    // Set user-level timezone override
    setUserTimezone,
    
    // Clear user override
    clearUserTimezone,
    
    // Check if user has override
    hasUserOverride: hasUserOverride(),
    
    // Get system default
    systemDefault: getSystemDefault(),
    
    // Available timezones
    availableTimezones: settings.availableTimezones,
    
    // Whether timezones are enabled
    enabled: settings.enableTimezones,
  };
}
